// SPDX-License-Identifier: AGPL-3.0-or-later

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import {
  AlertTriangle,
  FilePlus,
  FileText,
  Inbox,
  LayoutDashboard,
  Library,
  Menu,
  Palette,
  Settings,
  Sparkles,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@intakeform/ui';
import { BrandSymbol, BrandWordmark } from './BrandMark.js';
import { SidebarUserMenu } from './SidebarUserMenu.js';
import type { MeResponse } from '../api/types.js';

export type Me = MeResponse;

interface NavItem {
  to: string;
  label: string;
  Icon: LucideIcon;
}

/** Primary nav — surfaced to every clinician. Order matters: it dictates
 * tab order and the visual hierarchy in the sidebar. */
const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', Icon: LayoutDashboard },
  // Forms list lives at /forms (FormsRoute). The bare /builder
  // route renders a blank new-form draft — that's "New form" below.
  // Both used to point at /builder and clicking either landed on
  // the same blank canvas; the only visible difference was the URL
  // bar. Restored separation so the labels match where the user
  // ends up.
  { to: '/forms', label: 'Forms', Icon: FileText },
  { to: '/builder', label: 'New form', Icon: FilePlus },
  // Pointed at the nurse role's `flagged` tab because the badge on
  // this link counts flagged responses — landing on the matching
  // filter makes "1 in the badge → 1 here" obvious instead of
  // burying the flagged row in the default "All" view.
  { to: '/responses', label: 'Responses', Icon: Inbox },
  { to: '/templates', label: 'Template Library', Icon: Library },
  { to: '/autofill', label: 'EMR Autofill', Icon: Sparkles },
];

/** Admin-only nav — gated on `me.user.role === 'admin'`. */
const ADMIN_NAV_ITEMS: NavItem[] = [
  { to: '/settings', label: 'Settings', Icon: Settings },
  { to: '/settings/team', label: 'Team', Icon: Users },
];

/** Always-visible utilities (kept below admin so the order is sticky). */
const UTILITY_NAV_ITEMS: NavItem[] = [
  { to: '/design-system', label: 'Design system', Icon: Palette },
];

const MOBILE_BREAKPOINT_PX = 1024;

export interface SidebarProps {
  me: Me;
  onSignOut: () => void;
  isSigningOut?: boolean | undefined;
  /** Optional badge values keyed by nav `to`. e.g. { '/dashboard': 3 } */
  badges?: Record<string, number> | undefined;
}

/**
 * Clinician sidebar shell. Two layouts:
 *
 * - Desktop (≥ 1024px): a fixed-position ~240px column with a logo at
 *   the top, primary nav with icon-leading links and a warm-tinted
 *   active-route pill, and a bottom-pinned user menu (display name,
 *   theme toggle, sign-out).
 * - Mobile (< 1024px): the column collapses to a hamburger drawer.
 *   The drawer is an `aria-modal="true"` dialog; focus is trapped to
 *   the close button on open and Escape closes it.
 *
 * The shell keeps the in-i74 a11y machinery untouched — `:focus-visible`
 * rings, `skip-link`, and the `#main-content` focus target all come
 * from `ProtectedShell`.
 */
export function Sidebar({ me, onSignOut, isSigningOut, badges }: SidebarProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerCloseRef = useRef<HTMLButtonElement | null>(null);
  const drawerNavId = useId();
  const location = useLocation();

  // Close drawer on route change so a nav-link click doesn't leave it open.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Escape closes the drawer; focus the close button when it opens.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setDrawerOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    drawerCloseRef.current?.focus();
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen]);

  // If the viewport grows past the breakpoint while the drawer is open,
  // close it so the user doesn't end up with an open overlay on desktop.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia(`(min-width: ${MOBILE_BREAKPOINT_PX}px)`);
    const handler = (e: MediaQueryListEvent): void => {
      if (e.matches) setDrawerOpen(false);
    };
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, []);

  return (
    <>
      {/* Mobile-only top bar (hidden by CSS on desktop). */}
      <div data-ui-mobile-topbar="">
        <Button
          variant="ghost"
          aria-label="Open navigation"
          aria-expanded={drawerOpen}
          aria-controls={drawerNavId}
          onClick={() => setDrawerOpen(true)}
        >
          <Menu aria-hidden="true" size={20} />
        </Button>
        <BrandSymbol className="brand-symbol" aria-hidden="true" focusable="false" />
        <BrandWordmark className="brand-wordmark" />
      </div>

      {/* Desktop sidebar — always rendered; CSS hides it under the breakpoint. */}
      <aside data-ui-sidebar="" data-variant="desktop" aria-label="Primary navigation">
        <SidebarBody me={me} onSignOut={onSignOut} isSigningOut={isSigningOut} badges={badges} />
      </aside>

      {/* Mobile drawer — rendered only when open, with backdrop. */}
      {drawerOpen ? (
        <div data-ui-drawer-backdrop="" onClick={() => setDrawerOpen(false)}>
          <aside
            id={drawerNavId}
            data-ui-sidebar=""
            data-variant="drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Primary navigation"
            onClick={(e) => e.stopPropagation()}
          >
            <div data-ui-drawer-head="">
              <BrandSymbol className="brand-symbol" aria-hidden="true" focusable="false" />
              <BrandWordmark className="brand-wordmark" />
              <Button
                ref={drawerCloseRef}
                variant="ghost"
                aria-label="Close navigation"
                onClick={() => setDrawerOpen(false)}
              >
                <X aria-hidden="true" size={20} />
              </Button>
            </div>
            <SidebarBody
              me={me}
              onSignOut={onSignOut}
              isSigningOut={isSigningOut}
              badges={badges}
            />
          </aside>
        </div>
      ) : null}
    </>
  );
}

interface SidebarBodyProps {
  me: Me;
  onSignOut: () => void;
  isSigningOut?: boolean | undefined;
  badges?: Record<string, number> | undefined;
}

function SidebarBody({ me, onSignOut, isSigningOut, badges }: SidebarBodyProps) {
  return (
    <>
      <div data-ui-sidebar-brand="">
        <BrandSymbol className="brand-symbol" aria-hidden="true" focusable="false" />
        <BrandWordmark className="brand-wordmark" />
      </div>

      <nav data-ui-sidebar-nav="" aria-label="Primary">
        <ul>
          {NAV_ITEMS.map((item) => (
            <li key={item.to}>
              <SidebarLink to={item.to} Icon={item.Icon} badge={badges?.[item.to]}>
                {item.label}
              </SidebarLink>
            </li>
          ))}
        </ul>

        {me.user.role === 'admin' ? (
          <>
            <h3 data-ui-sidebar-section="">Administration</h3>
            <ul>
              {ADMIN_NAV_ITEMS.map((item) => (
                <li key={item.to}>
                  <SidebarLink to={item.to} Icon={item.Icon} badge={badges?.[item.to]}>
                    {item.label}
                  </SidebarLink>
                </li>
              ))}
            </ul>
          </>
        ) : null}

        <ul data-ui-sidebar-utility="">
          {UTILITY_NAV_ITEMS.map((item) => (
            <li key={item.to}>
              <SidebarLink to={item.to} Icon={item.Icon}>
                {item.label}
              </SidebarLink>
            </li>
          ))}
        </ul>
      </nav>

      <div data-ui-sidebar-foot="">
        <SidebarUserMenu me={me} onSignOut={onSignOut} isSigningOut={isSigningOut} />
      </div>
    </>
  );
}

interface SidebarLinkProps {
  to: string;
  Icon: LucideIcon;
  badge?: number | undefined;
  children: ReactNode;
}

function SidebarLink({ to, Icon, badge, children }: SidebarLinkProps) {
  return (
    <NavLink
      to={to}
      data-ui-sidebar-link=""
      className={({ isActive }) => (isActive ? 'active' : '')}
    >
      <Icon aria-hidden="true" size={18} data-ui-sidebar-link-icon="" />
      <span data-ui-sidebar-link-label="">{children}</span>
      {typeof badge === 'number' && badge > 0 ? (
        <span data-ui-sidebar-link-badge="" aria-label={`${badge.toString()} items`}>
          {badge}
        </span>
      ) : null}
    </NavLink>
  );
}

// Re-export the icon set so consumers don't need a transitive lucide-react
// import for the common ones.
export { AlertTriangle, FileText, Inbox };
